import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';
import { useSettings } from '@/hooks/useProducts';
import { useQuickEdit } from '@/hooks/useQuickEdit';
import QuickEditInline from '@/components/quickedit/QuickEditInline';
import { IS_LODGING } from '@/config/site';
import type { HeaderVariant } from '@/looks';
import { useSiteSettingMutation } from '@/hooks/useSiteSettingMutation';
import { useHeaderNav } from './headers/useHeaderNav';
import { useNavDropdowns, DesktopNavLink, navLinkKey } from './headers/NavLinks';
import BrandLogo, { useStoreName } from './headers/BrandLogo';
import HeaderActions from './headers/HeaderActions';
import MobileMenu from './headers/MobileMenu';

export interface HeaderProps {
  /**
   * Render the stripped-down "minimal chrome" variant: logo + Sign In +
   * Play Now CTA only. No search, cart, mobile menu, user menu, or nav.
   * Seeded for social_casino tenants by the wizard; tenants can opt-in via
   * site_settings.layout_chrome = 'minimal'.
   *
   * Equivalent to passing `variant="minimal"`; kept for backwards compatibility
   * with `layout_chrome === 'minimal'` callers.
   */
  minimal?: boolean;
  /**
   * Layout variant from the tenant's selected Look (theme_config.look).
   * - 'centered_logo' (default): logo centered above a horizontal nav row
   * - 'classic':                 logo-left with inline nav next to actions
   * - 'mega_menu':               centered_logo layout with a larger, normal-case nav row
   * - 'transparent_overlay':     centered_logo layout; visual transparency lands in Phase 3
   *                              once paired with a fullscreen_media hero
   * - 'minimal':                 same as the `minimal` prop above
   *
   * Variants with their own component (e.g. `floating_glass`) are dispatched
   * by `./header/index.tsx` before this component renders.
   */
  variant?: HeaderVariant;
}

/**
 * The storefront header: the five original Look variants. The nav assembly,
 * link renderers, actions cluster, brand logo and mobile menu live in
 * ./headers/* so other header variants compose the same pieces.
 */
export default function Header({ minimal: minimalProp = false, variant = 'centered_logo' }: HeaderProps = {}) {
  const minimal = minimalProp || variant === 'minimal';
  const isClassic = variant === 'classic';
  const isMega = variant === 'mega_menu';
  // Lodging on a centered-logo variant: render the nav inline on the LEFT of
  // the logo row (and drop the separate nav row below), so the header reads
  // nav-left · wordmark-center · actions-right. Without a left in-flow element
  // the actions cluster collapses to the left of the absolutely-centered logo.
  const isLodgingCentered = IS_LODGING && !isClassic && !minimal;
  const { t } = useTranslation(['nav', 'common']);
  const { user } = useAuthStore();
  const { isMobileMenuOpen, toggleMobileMenu, closeMobileMenu } = useUIStore();
  const { data: settingsData } = useSettings();
  const announcementText = settingsData?.settings?.announcement_bar_text;
  const announcementEnabled = settingsData?.settings?.announcement_bar_enabled;
  const { isActive: quickEditActive } = useQuickEdit();
  // One save path for every quick-edit field on this component: the hook
  // owns the cache invalidation, so it cannot be forgotten at a call site.
  const settingMutation = useSiteSettingMutation();
  const saveSetting = (settings: Record<string, unknown>) =>
    settingMutation.mutateAsync(settings);
  const [scrolled, setScrolled] = useState(false);
  const storeName = useStoreName();
  const nav = useHeaderNav();
  const { navLinks, visibleAppSections, inReplaceMode, showEcommerce, showCourses, LogoIcon } = nav;
  const dropdowns = useNavDropdowns(inReplaceMode);

  // Track scroll for header shadow
  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 10);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // AI-generated (or admin-set) brand logo for the minimal variant. Falls back
  // to the store name if unset or if the image fails to load.
  const [logoOk, setLogoOk] = useState(true);
  const logoUrl = logoOk ? ((settingsData?.settings?.store_logo_url as string | undefined) || undefined) : undefined;
  const logoDarkUrl = logoOk
    ? ((settingsData?.settings?.store_logo_dark_url as string | undefined) || undefined)
    : undefined;

  if (minimal) {
    return (
      <header
        className={cn(
          'sticky top-0 z-40 bg-surface/90 backdrop-blur border-b border-line transition-shadow',
          scrolled && 'shadow-sm',
        )}
        data-minimal-chrome
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center font-bold text-text-primary min-w-0">
            {logoUrl ? (
              <>
                <img
                  src={logoUrl}
                  alt={storeName}
                  onError={() => setLogoOk(false)}
                  className={cn(
                    'relative z-50 h-9 sm:h-10 w-auto max-w-[55vw] object-contain object-left',
                    logoDarkUrl && 'dark:hidden',
                  )}
                />
                {logoDarkUrl && (
                  <img
                    src={logoDarkUrl}
                    alt={storeName}
                    className="relative z-50 hidden h-9 sm:h-10 w-auto max-w-[55vw] object-contain object-left dark:block"
                  />
                )}
              </>
            ) : (
              <span className="truncate text-xl">{storeName}</span>
            )}
          </Link>
          <div className="flex items-center gap-3">
            {!user && (
              <Link
                to="/login"
                className="text-sm text-text-secondary hover:text-text-primary"
              >
                {t('common:signIn', { defaultValue: 'Sign In' })}
              </Link>
            )}
            {/* dark-exempt: intentional dark text on a non-flipping amber chip; reads in both modes */}
            <Link
              to="/play"
              className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-amber-400 text-stone-900 font-semibold text-sm rounded-full hover:bg-amber-300 transition-colors" /* dark-exempt */
            >
              {t('common:playNow', { defaultValue: 'Play Now' })}
            </Link>
          </div>
        </div>
      </header>
    );
  }

  return (
    <>
    <header
      className={cn('sticky top-0 z-40 bg-cream transition-shadow duration-300', scrolled && 'shadow-md')}
      data-header-variant={variant}
      data-scrolled={scrolled ? 'true' : 'false'}
      role="banner"
    >
      {/* Announcement bar */}
      {announcementEnabled && announcementText && (
        <div
          /* Deliberately-dark strip: surface-fixed-dark keeps it dark-brand in
             dark mode instead of inverting bright (sage-800 flips light). */
          className="surface-fixed-dark bg-sage-800 text-sage-100 text-xs text-center py-1.5 px-4 font-display italic tracking-wide-boutique"
          role="status"
          aria-live="polite"
        >
          {quickEditActive ? (
            <span className="inline-flex items-center gap-2">
              <QuickEditInline
                value={announcementText}
                onSave={async (val) => {
                  await saveSetting({ announcement_bar_text: val });
                }}
                label="Announcement bar text"
                as="span"
                className="text-sage-100 font-display italic"
              />
              {/* i18n-exempt: admin-only quick-edit shortcut, not visible to customers */}
              <Link to="/admin/settings/theme" className="ml-1 text-sage-300 hover:text-white underline text-xs not-italic">
                Theme
              </Link>
            </span>
          ) : (
            announcementText
          )}
        </div>
      )}

      {/* Logo row */}
      <div className="bg-cream border-b border-sage-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={cn('flex items-center h-16 lg:h-20', isClassic ? 'justify-between gap-6' : 'justify-between')}>
            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 -ml-2 text-sage-800 hover:text-sage-900 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
              onClick={toggleMobileMenu}
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? t('nav:closeMenu') : t('nav:openMenu')}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>

            {/* Logo —
               classic: left-aligned, no absolute positioning, paired with inline desktop nav
               centered_logo / mega_menu / transparent_overlay: centered (absolute on desktop)
            */}
            {/*
              min-w-0 lets the logo container shrink below its content's
              natural width when the row gets tight — without it the
              actions cluster pushes the row past the viewport on narrow
              phones (LSG's store name + 4 icon buttons overflows ~375px
              viewports), which forces a horizontal page scroll and the
              white gutter to the right of embedded app iframes.
            */}
            <div
              className={cn(
                'min-w-0',
                isClassic
                  ? 'flex justify-start'
                  : 'flex-1 flex justify-center lg:flex-none lg:absolute lg:left-1/2 lg:-translate-x-1/2',
              )}
            >
              <BrandLogo Icon={LogoIcon} onClick={closeMobileMenu} />
            </div>

            {/* Classic-variant inline nav (desktop only) — sits between logo and actions.
                Other variants render the nav in the dedicated row below. */}
            {isClassic && (
              <nav
                className="hidden lg:flex items-center gap-6 flex-1 justify-center"
                aria-label={t('nav:mainNavigation')}
              >
                {navLinks.map((link) => (
                  <DesktopNavLink
                    key={navLinkKey(link)}
                    link={link}
                    dropdowns={dropdowns}
                    linkClass="text-sm font-sans text-sage-800 hover:text-sage-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded px-1 py-1"
                  />
                ))}
              </nav>
            )}

            {/* Lodging centered-logo variant: invisible spacer on the LEFT
                (desktop only) so `justify-between` pushes the actions cluster
                to the right while the logo stays absolutely centered. The real
                nav lives in the dedicated row below — putting the full nav
                inline here collides with the centered wordmark on narrower
                desktops (e.g. "The Juliet" + 5 items at 1024–1200px). */}
            {isLodgingCentered && (
              <div className="hidden lg:block flex-1" aria-hidden="true" />
            )}

            {/* Actions */}
            <HeaderActions
              visibleAppSections={visibleAppSections}
              showEcommerce={showEcommerce}
              showCourses={showCourses}
            />
          </div>
        </div>
      </div>

      {/* Desktop nav row — hidden for `classic` (nav is inline in the logo row).
          `mega_menu` uses a larger, normal-case treatment; `centered_logo` /
          `transparent_overlay` keep the original compact uppercase style.
          Skipped when there are no links (e.g. inside a replace-mode app mount
          or with hide_nav_when_authed) so it doesn't leave an empty strip. */}
      {!isClassic && navLinks.length > 0 && (
        <nav
          className="hidden lg:block bg-cream border-b border-sage-200"
          aria-label={t('nav:mainNavigation')}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={cn('flex items-center justify-center', isMega ? 'gap-10 h-14' : 'gap-8 h-11')}>
              {navLinks.map((link) => (
                <DesktopNavLink
                  key={navLinkKey(link)}
                  link={link}
                  dropdowns={dropdowns}
                  linkClass={cn(
                    'font-sans text-sage-800 hover:text-sage-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded px-1 py-1',
                    isMega ? 'text-sm font-medium' : 'text-xs uppercase tracking-wide-boutique',
                  )}
                />
              ))}
            </div>
          </div>
        </nav>
      )}

    </header>

      {/* Mobile menu — solid slide-in drawer over a dimmed backdrop. Rendered
          as a sibling of <header> (not in-flow) so it overlays page content at
          z-50 with an opaque panel, instead of expanding the sticky header. */}
      <MobileMenu
        navLinks={navLinks}
        visibleAppSections={visibleAppSections}
        showEcommerce={showEcommerce}
        showCourses={showCourses}
        storeName={storeName}
        inReplaceMode={inReplaceMode}
        dropdowns={dropdowns}
      />
    </>
  );
}
